/**
 * Copyright (c) 2025 Kalpit Patel
 * Public Events page – events from Associations, NGOs (and communities).
 * Card grid + calendar view, orange-teal theme.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faMapMarkerAlt,
  faBookmark,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faClock,
  faUsers,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { buildApiUrl } from '../config/api';
import { formatEventTimeDisplay } from '../utils/eventTime';
import { formatEventStatusLabel, getEventStatus } from '../utils/eventUtils';
import '../styles/EventsPage.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EventStatusTag = ({ event }) => {
  const label = formatEventStatusLabel(event?.status);
  if (!label) return null;
  return (
    <span className={`events-card-status-tag ${getEventStatus(event)}`}>
      {label}
    </span>
  );
};

function parseEventDate(isoStr) {
  if (isoStr == null || isoStr === '') return null;
  try {
    const s = typeof isoStr === 'string' ? isoStr.trim() : String(isoStr);
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function Events() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sort, setSort] = useState('latest');
  const [viewMode, setViewMode] = useState('list'); // list | month | day | saved
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // controlled input; search runs on Find Events
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dayViewDate, setDayViewDate] = useState(() => new Date());
  const [savedIds, setSavedIds] = useState(() => {
    try {
      const s = localStorage.getItem('akshar_events_saved');
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const EVENTS_PAGE_SIZE = 24;

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await axios.get(buildApiUrl(`/api/public/events?sort=${sort}&limit=${EVENTS_PAGE_SIZE}&skip=0`));
        const list = res.data?.success && Array.isArray(res.data.events) ? res.data.events : [];
        setEvents(list);
        setTotalEvents(typeof res.data?.total === 'number' ? res.data.total : list.length);
      } catch (err) {
        setEvents([]);
        setTotalEvents(0);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [sort]);

  // Open event modal when URL has ?event=<id> (e.g. from shared WhatsApp link)
  useEffect(() => {
    const eventId = searchParams.get('event');
    if (!eventId || !events.length) return;
    const ev = events.find((e) => String(e.id || e._id) === String(eventId));
    if (ev) setSelectedEvent(ev);
  }, [events, searchParams]);

  const loadMoreEvents = async () => {
    if (loadingMore || events.length >= totalEvents) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(buildApiUrl(`/api/public/events?sort=${sort}&limit=${EVENTS_PAGE_SIZE}&skip=${events.length}`));
      const list = res.data?.success && Array.isArray(res.data.events) ? res.data.events : [];
      setEvents((prev) => [...prev, ...list]);
      if (typeof res.data?.total === 'number') setTotalEvents(res.data.total);
    } catch (err) {
      // keep current events on error
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMoreEvents = events.length < totalEvents && totalEvents > 0;

  const toggleSaved = (id) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        localStorage.setItem('akshar_events_saved', JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const shareEventOnWhatsApp = (ev) => {
    if (!ev) return;
    try {
      const dateRaw = ev.eventDate ?? ev.date;
      const d = parseEventDate(dateRaw);
      const dateStr = d
        ? d.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
        : '';
      const timeStr = formatEventTimeDisplay(ev.eventTime || ev.time || '');
      const locationStr = ev.location || ev.venue || '';
      const organizerStr = ev.organizerName || ev.postedBy || '';
      const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
      const baseUrl = origin || 'https://aksharjobs.com';
      const eventId = ev.id || ev._id;
      const eventUrl = eventId ? `${baseUrl}/events?event=${encodeURIComponent(eventId)}` : `${baseUrl}/events`;

      const desc = ev.shortDescription || ev.description || '';
      const descSnippet = desc ? (desc.length > 200 ? desc.slice(0, 200).trim() + '...' : desc) : '';

      const parts = [
        'AksharJobs Events',
        '',
        ev.title || 'Event',
        [dateStr, timeStr].filter(Boolean).join(' · ') || '',
        locationStr ? `Location: ${locationStr}` : '',
        organizerStr ? `By ${organizerStr}` : '',
        '',
        descSnippet || '',
        '',
        'View full event & poster:',
        eventUrl
      ].filter((line) => line !== '');

      const text = parts.join('\n');
      const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // ignore share errors
    }
  };

  const calendarDays = useMemo(() => {
    const { year, month } = calendarMonth;
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startPad = first.getDay();
    const daysInMonth = last.getDate();
    const days = [];
    for (let i = 0; i < startPad; i++) {
      const d = new Date(year, month, -startPad + i + 1);
      days.push({ date: d, otherMonth: true, dayNum: d.getDate() });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), otherMonth: false, dayNum: i });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push({ date: d, otherMonth: true, dayNum: d.getDate() });
    }
    return days;
  }, [calendarMonth]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      const d = parseEventDate(ev.eventDate);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;
    const q = searchQuery.trim().toLowerCase();
    return events.filter(
      (ev) =>
        (ev.title || '').toLowerCase().includes(q) ||
        (ev.description || '').toLowerCase().includes(q) ||
        (ev.shortDescription || '').toLowerCase().includes(q) ||
        (ev.organizerName || ev.postedBy || '').toLowerCase().includes(q) ||
        (ev.location || '').toLowerCase().includes(q) ||
        (ev.venue || '').toLowerCase().includes(q) ||
        (ev.eventType || '').toLowerCase().includes(q)
    );
  }, [events, searchQuery]);

  const savedEvents = useMemo(
    () => events.filter((ev) => savedIds.includes(ev.id)),
    [events, savedIds]
  );

  const savedEventsFiltered = useMemo(() => {
    if (!searchQuery.trim()) return savedEvents;
    const q = searchQuery.trim().toLowerCase();
    return savedEvents.filter(
      (ev) =>
        (ev.title || '').toLowerCase().includes(q) ||
        (ev.description || '').toLowerCase().includes(q) ||
        (ev.shortDescription || '').toLowerCase().includes(q) ||
        (ev.organizerName || ev.postedBy || '').toLowerCase().includes(q) ||
        (ev.location || '').toLowerCase().includes(q) ||
        (ev.venue || '').toLowerCase().includes(q) ||
        (ev.eventType || '').toLowerCase().includes(q)
    );
  }, [savedEvents, searchQuery]);

  const dayViewKey = `${dayViewDate.getFullYear()}-${dayViewDate.getMonth()}-${dayViewDate.getDate()}`;
  const dayViewEvents = useMemo(
    () => eventsByDate[dayViewKey] || [],
    [eventsByDate, dayViewKey]
  );

  const runSearch = () => setSearchQuery(searchInput.trim());
  const handleSearchKeyDown = (e) => e.key === 'Enter' && runSearch();

  const todayKey = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${t.getMonth()}-${t.getDate()}`;
  }, []);

  const prevMonth = () => {
    setCalendarMonth((prev) => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCalendarMonth((prev) => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  const monthTitle = `${MONTHS[calendarMonth.month]} ${calendarMonth.year}`;

  return (
    <div className="events-page">
      <header className="events-page-header">
        <button
          type="button"
          className="events-page-back"
          onClick={() => navigate('/')}
          aria-label="Back to home"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="events-page-header-inner">
          <h1 className="events-page-title">Upcoming Events</h1>
        </div>
      </header>

      {/* Search bar + sort dropdown + List / Month / Day */}
      <div className="events-search-bar">
        <div className="events-search-bar-inner">
          <input
            type="text"
            className="events-search-input"
            placeholder="Search for events"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            aria-label="Search for events"
          />
          <button type="button" className="events-find-btn" onClick={runSearch}>
            FIND EVENTS
          </button>
          <select
            className="events-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort events"
          >
            <option value="latest">Latest</option>
            <option value="popular">Popular</option>
            <option value="weekdays">This Week</option>
          </select>
          <div className="events-view-tabs">
            <button
              type="button"
              className={`events-view-tab ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              type="button"
              className={`events-view-tab ${viewMode === 'saved' ? 'active' : ''}`}
              onClick={() => setViewMode('saved')}
            >
              <FontAwesomeIcon icon={faBookmark} style={{ marginRight: '0.35rem' }} />
              Saved
              {savedIds.length > 0 && (
                <span className="events-view-tab-badge" aria-label={`${savedIds.length} saved`}>
                  {savedIds.length}
                </span>
              )}
            </button>
            <button
              type="button"
              className={`events-view-tab ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button
              type="button"
              className={`events-view-tab ${viewMode === 'day' ? 'active' : ''}`}
              onClick={() => setViewMode('day')}
            >
              Day
            </button>
          </div>
        </div>
      </div>

      <div className="events-page-content">
        {loading ? (
          <div className="events-loading">
            <div className="events-loading-spinner" />
            <p>Loading events…</p>
          </div>
        ) : viewMode === 'month' ? (
          <div className="events-calendar-wrap">
            <div className="events-calendar-header">
              <h2 className="events-calendar-month-title">{monthTitle}</h2>
              <div className="events-calendar-nav">
                <button type="button" onClick={prevMonth} aria-label="Previous month">
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button type="button" onClick={nextMonth} aria-label="Next month">
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
            <div className="events-calendar-grid">
              {WEEKDAYS.map((wd) => (
                <div key={wd} className="events-calendar-weekday">
                  {wd}
                </div>
              ))}
              {calendarDays.map((cell, idx) => {
                const key = `${cell.date.getFullYear()}-${cell.date.getMonth()}-${cell.date.getDate()}`;
                const dayEvents = eventsByDate[key] || [];
                const isToday = key === todayKey;
                return (
                  <div
                    key={idx}
                    className={`events-calendar-day ${cell.otherMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${dayEvents.length ? 'has-events' : ''}`}
                  >
                    <div className="events-calendar-day-num">{cell.dayNum}</div>
                    <div className="events-calendar-day-events">
                      {dayEvents.slice(0, 3).map((ev) => (
                        <div
                          key={ev.id}
                          className="events-calendar-day-event"
                          onClick={() => setSelectedEvent(ev)}
                          title={ev.title}
                        >
                          {ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="events-calendar-day-event">+{dayEvents.length - 3}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : viewMode === 'day' ? (
          <div className="events-day-wrap">
            <div className="events-day-header">
              <button type="button" className="events-day-nav" onClick={() => setDayViewDate((d) => new Date(d.getTime() - 86400000))} aria-label="Previous day">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <h2 className="events-day-title">
                {dayViewDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
              <button type="button" className="events-day-nav" onClick={() => setDayViewDate((d) => new Date(d.getTime() + 86400000))} aria-label="Next day">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
            {dayViewEvents.length === 0 ? (
              <p className="events-day-empty">No events on this day.</p>
            ) : (
              <div className="events-day-list">
                {dayViewEvents.map((ev) => {
                  const d = parseEventDate(ev.eventDate);
                  const isSaved = savedIds.includes(ev.id);
                  return (
                    <article
                      key={ev.id}
                      className="events-day-card"
                      onClick={() => setSelectedEvent(ev)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedEvent(ev)}
                    >
                      <div className="events-day-card-time">{formatEventTimeDisplay(ev.eventTime) || '—'}</div>
                      <div className="events-day-card-body">
                        <h3 className="events-day-card-title">{ev.title}</h3>
                        <p className="events-day-card-organizer">By {ev.organizerName || ev.postedBy || '—'}</p>
                        {(ev.location || ev.venue) && (
                          <p className="events-day-card-location">
                            <FontAwesomeIcon icon={faMapMarkerAlt} /> {ev.location || ev.venue}
                          </p>
                        )}
                      </div>
                      <div className="events-card-actions">
                        <button
                          type="button"
                          className={`events-card-bookmark ${isSaved ? 'saved' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSaved(ev.id);
                          }}
                          aria-label={isSaved ? 'Unsave' : 'Save'}
                        >
                          <FontAwesomeIcon icon={faBookmark} />
                        </button>
                        <button
                          type="button"
                          className="events-card-share"
                          onClick={(e) => {
                            e.stopPropagation();
                            shareEventOnWhatsApp(ev);
                          }}
                          aria-label="Share event on WhatsApp"
                        >
                          <FontAwesomeIcon icon={faWhatsapp} />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        ) : viewMode === 'saved' && savedEventsFiltered.length === 0 ? (
          <div className="events-empty">
            <div className="events-empty-icon-wrap">
              <FontAwesomeIcon icon={faBookmark} className="events-empty-icon" />
            </div>
            <h3 className="events-empty-title">
              {searchQuery ? 'No saved events match your search' : 'No saved events'}
            </h3>
            <p className="events-empty-text">
              {searchQuery
                ? 'Try a different search term or switch to List to save more events.'
                : 'Save events from the List view by clicking the bookmark icon on any event card.'}
            </p>
          </div>
        ) : viewMode === 'saved' ? (
          <>
            <div className="events-grid">
              {savedEventsFiltered.map((ev) => {
                const dateRaw = ev.eventDate ?? ev.date;
                const d = parseEventDate(dateRaw);
                const monthStr = d ? MONTHS[d.getMonth()].substring(0, 3).toUpperCase() : '—';
                const dayNum = d ? d.getDate() : '—';
                const timeStr = formatEventTimeDisplay(ev.eventTime || ev.time || '');
                const isSaved = true;
                return (
                  <article
                    key={ev.id}
                    className="events-card"
                    onClick={() => setSelectedEvent(ev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedEvent(ev)}
                  >
                    <div className="events-card-poster-wrap">
                      {ev.posterUrl ? (
                        <img src={ev.posterUrl} alt="" />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '1.25rem',
                            fontWeight: 700
                          }}
                        >
                          {ev.eventType || 'Event'}
                        </div>
                      )}
                      <EventStatusTag event={ev} />
                    </div>
                    <div className="events-card-body">
                      <div className="events-card-date-title">
                        <div className="events-card-date">
                          <span className="month">{monthStr}</span>
                          <span className="day">{dayNum}</span>
                          {timeStr ? <span className="events-card-time">{timeStr}</span> : null}
                        </div>
                        <h3 className="events-card-title">{ev.title}</h3>
                      </div>
                      <p className="events-card-organizer">
                        By {ev.organizerName || ev.postedBy || '—'}
                      </p>
                      {ev.shortDescription && (
                        <p className="events-card-short-desc">{ev.shortDescription}</p>
                      )}
                      <div className="events-card-footer">
                        <span className="events-card-location">
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          <span className="events-card-location-text">
                            {ev.location || ev.venue || 'TBA'}
                          </span>
                        </span>
                        <div className="events-card-actions">
                          <button
                            type="button"
                            className="events-card-bookmark saved"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSaved(ev.id);
                            }}
                            aria-label="Unsave event"
                          >
                            <FontAwesomeIcon icon={faBookmark} />
                          </button>
                          <button
                            type="button"
                            className="events-card-share"
                            onClick={(e) => {
                              e.stopPropagation();
                              shareEventOnWhatsApp(ev);
                            }}
                            aria-label="Share event on WhatsApp"
                          >
                            <FontAwesomeIcon icon={faWhatsapp} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            {hasMoreEvents && (
              <div className="events-see-all-wrap">
                <button
                  type="button"
                  className="events-see-all-btn"
                  onClick={loadMoreEvents}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                      Loading…
                    </>
                  ) : (
                    'Load more events'
                  )}
                </button>
              </div>
            )}
          </>
        ) : filteredEvents.length === 0 ? (
          <div className="events-empty">
            <div className="events-empty-icon-wrap">
              <FontAwesomeIcon icon={faCalendarAlt} className="events-empty-icon" />
            </div>
            <h3 className="events-empty-title">{searchQuery ? 'No events match your search' : 'No events'}</h3>
            <p className="events-empty-text">
              {searchQuery ? 'Try a different search term.' : 'Check back later for events from associations, NGOs, and communities.'}
            </p>
          </div>
        ) : (
          <>
            <div className="events-grid">
              {filteredEvents.map((ev) => {
                const dateRaw = ev.eventDate ?? ev.date;
                const d = parseEventDate(dateRaw);
                const monthStr = d ? MONTHS[d.getMonth()].substring(0, 3).toUpperCase() : '—';
                const dayNum = d ? d.getDate() : '—';
                const timeStr = formatEventTimeDisplay(ev.eventTime || ev.time || '');
                const isSaved = savedIds.includes(ev.id);
                return (
                  <article
                    key={ev.id}
                    className="events-card"
                    onClick={() => setSelectedEvent(ev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedEvent(ev)}
                  >
                    <div className="events-card-poster-wrap">
                      {ev.posterUrl ? (
                        <img src={ev.posterUrl} alt="" />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '1.25rem',
                            fontWeight: 700
                          }}
                        >
                          {ev.eventType || 'Event'}
                        </div>
                      )}
                      <EventStatusTag event={ev} />
                    </div>
                    <div className="events-card-body">
                      <div className="events-card-date-title">
                        <div className="events-card-date">
                          <span className="month">{monthStr}</span>
                          <span className="day">{dayNum}</span>
                          {timeStr ? <span className="events-card-time">{timeStr}</span> : null}
                        </div>
                        <h3 className="events-card-title">{ev.title}</h3>
                      </div>
                      <p className="events-card-organizer">
                        By {ev.organizerName || ev.postedBy || '—'}
                      </p>
                      {ev.shortDescription && (
                        <p className="events-card-short-desc">{ev.shortDescription}</p>
                      )}
                      <div className="events-card-footer">
                        <span className="events-card-location">
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          <span className="events-card-location-text">
                            {ev.location || ev.venue || 'TBA'}
                          </span>
                        </span>
                        <div className="events-card-actions">
                          <button
                            type="button"
                            className={`events-card-bookmark ${isSaved ? 'saved' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSaved(ev.id);
                            }}
                            aria-label={isSaved ? 'Unsave event' : 'Save event'}
                          >
                            <FontAwesomeIcon icon={faBookmark} />
                          </button>
                          <button
                            type="button"
                            className="events-card-share"
                            onClick={(e) => {
                              e.stopPropagation();
                              shareEventOnWhatsApp(ev);
                            }}
                            aria-label="Share event on WhatsApp"
                          >
                            <FontAwesomeIcon icon={faWhatsapp} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            {hasMoreEvents && (
              <div className="events-see-all-wrap">
                <button
                  type="button"
                  className="events-see-all-btn"
                  onClick={loadMoreEvents}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                      Loading…
                    </>
                  ) : (
                    'Load more events'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedEvent && (
        <div
          className="events-modal-overlay"
          onClick={() => setSelectedEvent(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="event-modal-title"
        >
          <div className="events-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="events-modal-close"
              onClick={() => setSelectedEvent(null)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            {selectedEvent.posterUrl ? (
              <div className="events-modal-poster-wrap">
                <img src={selectedEvent.posterUrl} alt="" className="events-modal-poster" />
              </div>
            ) : (
              <div className="events-modal-poster events-modal-poster--placeholder">
                {selectedEvent.eventType || 'Event'}
              </div>
            )}
            <div className="events-modal-body">
              <h2 id="event-modal-title" className="events-modal-title">
                {selectedEvent.title}
              </h2>
              <div className="events-modal-meta">
                {formatEventStatusLabel(selectedEvent.status) && (
                  <span className={`events-modal-status ${getEventStatus(selectedEvent)}`}>
                    {formatEventStatusLabel(selectedEvent.status)}
                  </span>
                )}
                <span>
                  <FontAwesomeIcon icon={faUsers} />
                  {selectedEvent.organizerName || selectedEvent.postedBy || '—'}
                </span>
                {selectedEvent.eventDate && (
                  <span>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {parseEventDate(selectedEvent.eventDate)?.toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                )}
                {selectedEvent.eventTime && (
                  <span>
                    <FontAwesomeIcon icon={faClock} />
                    {formatEventTimeDisplay(selectedEvent.eventTime)}
                  </span>
                )}
                {(selectedEvent.venue || selectedEvent.location) && (
                  <span>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {selectedEvent.venue || selectedEvent.location}
                  </span>
                )}
              </div>
              {selectedEvent.description ? (
                <div className="events-modal-description">{selectedEvent.description}</div>
              ) : (
                <p className="events-modal-description">No detailed description available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;
